import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ChangeCurrencyOptions,
  CreateTicketDefinitionOptions,
  CreateTicketDefinitionResponse,
  DefinitionsQueryBuilder,
  DeleteTicketDefinitionOptions,
  GetTicketDefinitionOptions,
  ListTicketDefinitionsOptions,
  ListTicketDefinitionsResponse,
  QueryTicketDefinitionsOptions,
  QueryTicketDefinitionsResponse,
  QueryTicketDefinitionsV2Options,
  QueryTicketDefinitionsV2Response,
  TicketDefinition,
  TicketDefinitionCreatedEnvelope,
  TicketDefinitionDeletedEnvelope,
  TicketDefinitionQuery,
  TicketDefinitionUpdatedEnvelope,
  UpdateTicketDefinitionOptions,
  UpdateTicketDefinitionResponse,
  changeCurrency as universalChangeCurrency,
  createTicketDefinition as universalCreateTicketDefinition,
  deleteTicketDefinition as universalDeleteTicketDefinition,
  getTicketDefinition as universalGetTicketDefinition,
  listTicketDefinitions as universalListTicketDefinitions,
  queryTicketDefinitions as universalQueryTicketDefinitions,
  queryTicketDefinitionsV2 as universalQueryTicketDefinitionsV2,
  typedQueryTicketDefinitionsV2 as universalTypedQueryTicketDefinitionsV2,
  updateTicketDefinition as universalUpdateTicketDefinition,
} from './events-v1-ticket-definition-ticket-definitions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function queryTicketDefinitions(
  httpClient: HttpClient
): QueryTicketDefinitionsSignature {
  return (options?: QueryTicketDefinitionsOptions) =>
    universalQueryTicketDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTicketDefinitionsSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available. Use [`queryTicketDefinitions()`](/ticket-definitions-v2/query-ticket-definitions) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`queryTicketDefinition()`](/ticket-definitions-v2/query-ticket-definitions).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.queryTicketDefinition()`, and replace it with `ticketDefinitionsV2.queryTicketDefinition()`. Update your code to work with the new `createTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Retrieves a list of up to 100 ticket definitions.
   * @deprecated
   */
  (options?: QueryTicketDefinitionsOptions): Promise<
    NonNullablePaths<
      QueryTicketDefinitionsResponse,
      | `total`
      | `offset`
      | `limit`
      | `definitions`
      | `definitions.${number}._id`
      | `definitions.${number}.price.amount`
      | `definitions.${number}.price.currency`
      | `definitions.${number}.free`
      | `definitions.${number}.name`
      | `definitions.${number}.description`
      | `definitions.${number}.limitPerCheckout`
      | `definitions.${number}.orderIndex`
      | `definitions.${number}.policy`
      | `definitions.${number}.dashboard.hidden`
      | `definitions.${number}.dashboard.sold`
      | `definitions.${number}.dashboard.limited`
      | `definitions.${number}.dashboard.ticketsSold`
      | `definitions.${number}.dashboard.ticketsReserved`
      | `definitions.${number}.eventId`
      | `definitions.${number}.wixFeeConfig.type`
      | `definitions.${number}.salePeriod.hideNotOnSale`
      | `definitions.${number}.saleStatus`
      | `definitions.${number}.pricing.pricingType`,
      5
    >
  >;
}

export function queryTicketDefinitionsV2(
  httpClient: HttpClient
): QueryTicketDefinitionsV2Signature {
  return (options?: QueryTicketDefinitionsV2Options) =>
    universalQueryTicketDefinitionsV2(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTicketDefinitionsV2Signature {
  /**
   * **Deprecated.** This function will continue to work until October 29, 2024, but a newer version is available at [`queryTicketDefinitions()`](/ticket-definitions-v2/query-ticket-definitions).
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`queryTicketDefinitions()`](/ticket-definitions-v2/query-ticket-definitions).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.queryTicketDefinitions()`, and replace it with `ticketDefinitionsV2.queryTicketDefinitions()`. Update your code to work with the new `queryTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Retrieves a list of up to 1,000 ticket definitions, given the provided paging and filtering.
   * @deprecated
   */
  (options?: QueryTicketDefinitionsV2Options): DefinitionsQueryBuilder;
}

export function typedQueryTicketDefinitionsV2(
  httpClient: HttpClient
): TypedQueryTicketDefinitionsV2Signature {
  return (
    query: TicketDefinitionQuery,
    options?: QueryTicketDefinitionsV2Options
  ) =>
    universalTypedQueryTicketDefinitionsV2(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTicketDefinitionsV2Signature {
  /** */
  (
    query: TicketDefinitionQuery,
    options?: QueryTicketDefinitionsV2Options
  ): Promise<
    NonNullablePaths<
      QueryTicketDefinitionsV2Response,
      | `definitions`
      | `definitions.${number}._id`
      | `definitions.${number}.price.amount`
      | `definitions.${number}.price.currency`
      | `definitions.${number}.free`
      | `definitions.${number}.name`
      | `definitions.${number}.description`
      | `definitions.${number}.limitPerCheckout`
      | `definitions.${number}.orderIndex`
      | `definitions.${number}.policy`
      | `definitions.${number}.dashboard.hidden`
      | `definitions.${number}.dashboard.sold`
      | `definitions.${number}.dashboard.limited`
      | `definitions.${number}.dashboard.ticketsSold`
      | `definitions.${number}.dashboard.ticketsReserved`
      | `definitions.${number}.eventId`
      | `definitions.${number}.wixFeeConfig.type`
      | `definitions.${number}.salePeriod.hideNotOnSale`
      | `definitions.${number}.saleStatus`
      | `definitions.${number}.pricing.pricingType`,
      5
    >
  >;
}

export function listTicketDefinitions(
  httpClient: HttpClient
): ListTicketDefinitionsSignature {
  return (options?: ListTicketDefinitionsOptions) =>
    universalListTicketDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListTicketDefinitionsSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a substitute is available. Use the [`queryTicketDefinitions()`](/ticket-definitions-v2/query-ticket-definitions) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`queryTicketDefinitions()`](/ticket-definitions-v2/query-ticket-definitions).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.queryTicketDefinitions()`, and replace it with `ticketDefinitionsV2.queryTicketDefinitions()`. Update your code to work with the new `queryTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Retrieves a list of up to 100 ticket definitions, with basic filter support.
   * @param - Details for the tickets to retrieve.
   * @deprecated
   */
  (options?: ListTicketDefinitionsOptions): Promise<
    NonNullablePaths<
      ListTicketDefinitionsResponse,
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`
      | `definitions`
      | `definitions.${number}._id`
      | `definitions.${number}.price.amount`
      | `definitions.${number}.price.currency`
      | `definitions.${number}.free`
      | `definitions.${number}.name`
      | `definitions.${number}.description`
      | `definitions.${number}.limitPerCheckout`
      | `definitions.${number}.orderIndex`
      | `definitions.${number}.policy`
      | `definitions.${number}.dashboard.hidden`
      | `definitions.${number}.dashboard.sold`
      | `definitions.${number}.dashboard.limited`
      | `definitions.${number}.dashboard.ticketsSold`
      | `definitions.${number}.dashboard.ticketsReserved`
      | `definitions.${number}.eventId`
      | `definitions.${number}.wixFeeConfig.type`
      | `definitions.${number}.salePeriod.hideNotOnSale`
      | `definitions.${number}.saleStatus`
      | `definitions.${number}.pricing.pricingType`,
      5
    >
  >;
}

export function getTicketDefinition(
  httpClient: HttpClient
): GetTicketDefinitionSignature {
  return (definitionId: string, options?: GetTicketDefinitionOptions) =>
    universalGetTicketDefinition(
      definitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTicketDefinitionSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available. Use the [`getTicketDefinition()`](/ticket-definitions-v2/get-ticket-definition) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`getTicketDefinition()`](/ticket-definitions-v2/get-ticket-definition).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.getTicketDefinition()`, and replace it with `ticketDefinitionsV2.getTicketDefinition()`. Update your code to work with the new `getTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Retrieves a ticket definition.
   * @param - Ticket definition ID.
   * @param - Details for the ticket to retrieve.
   * @returns Retrieved ticket definition.
   * @deprecated
   */
  (definitionId: string, options?: GetTicketDefinitionOptions): Promise<
    NonNullablePaths<
      TicketDefinition,
      | `_id`
      | `price.amount`
      | `price.currency`
      | `free`
      | `name`
      | `description`
      | `limitPerCheckout`
      | `orderIndex`
      | `policy`
      | `dashboard.hidden`
      | `dashboard.sold`
      | `dashboard.limited`
      | `dashboard.ticketsSold`
      | `dashboard.ticketsReserved`
      | `eventId`
      | `wixFeeConfig.type`
      | `salePeriod.hideNotOnSale`
      | `saleStatus`
      | `state`
      | `pricing.pricingOptions.options`
      | `pricing.pricingType`,
      4
    >
  >;
}

export function createTicketDefinition(
  httpClient: HttpClient
): CreateTicketDefinitionSignature {
  return (
    eventId: string,
    options: NonNullablePaths<
      CreateTicketDefinitionOptions,
      `definition` | `definition.name`,
      3
    >
  ) =>
    universalCreateTicketDefinition(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTicketDefinitionSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available. Use the [`createTicketDefinition()`](/ticket-definitions-v2/create-ticket-definition) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`createTicketDefinition()`](/ticket-definitions-v2/create-ticket-definition).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.createTicketDefinition()`, and replace it with `ticketDefinitionsV2.createTicketDefinition()`. Update your code to work with the new `createTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Creates a ticket definition (and enables ticket sales).
   * @param - Event ID.
   * @deprecated
   */
  (
    eventId: string,
    options: NonNullablePaths<
      CreateTicketDefinitionOptions,
      `definition` | `definition.name`,
      3
    >
  ): Promise<
    NonNullablePaths<
      CreateTicketDefinitionResponse,
      | `definition._id`
      | `definition.price.amount`
      | `definition.price.currency`
      | `definition.free`
      | `definition.name`
      | `definition.description`
      | `definition.limitPerCheckout`
      | `definition.orderIndex`
      | `definition.policy`
      | `definition.dashboard.hidden`
      | `definition.dashboard.sold`
      | `definition.dashboard.limited`
      | `definition.dashboard.ticketsSold`
      | `definition.dashboard.ticketsReserved`
      | `definition.eventId`
      | `definition.wixFeeConfig.type`
      | `definition.salePeriod.hideNotOnSale`
      | `definition.saleStatus`
      | `definition.state`
      | `definition.pricing.pricingOptions.options`
      | `definition.pricing.pricingType`,
      5
    >
  >;
}

export function updateTicketDefinition(
  httpClient: HttpClient
): UpdateTicketDefinitionSignature {
  return (
    definitionId: string,
    eventId: string,
    options?: UpdateTicketDefinitionOptions
  ) =>
    universalUpdateTicketDefinition(
      definitionId,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTicketDefinitionSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available. Use the [`updateTicketDefinition()`](/ticket-definitions-v2/update-ticket-definition) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`updateTicketDefinition()`](/ticket-definitions-v2/update-ticket-definition).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.updateTicketDefinition()`, and replace it with `ticketDefinitionsV2.updateTicketDefinition()`. Update your code to work with the new `updateTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   *
   * Updates a ticket definition.
   *
   * See [Partial Updates](/wix-events-v2/partial-updates) for more information.
   * @param - Ticket definition ID.
   * @param - Event ID.
   * @param - Details of the ticket definition to update.
   * @param - Ticket definition details to update.
   * @deprecated
   */
  (
    definitionId: string,
    eventId: string,
    options?: UpdateTicketDefinitionOptions
  ): Promise<
    NonNullablePaths<
      UpdateTicketDefinitionResponse,
      | `definition._id`
      | `definition.price.amount`
      | `definition.price.currency`
      | `definition.free`
      | `definition.name`
      | `definition.description`
      | `definition.limitPerCheckout`
      | `definition.orderIndex`
      | `definition.policy`
      | `definition.dashboard.hidden`
      | `definition.dashboard.sold`
      | `definition.dashboard.limited`
      | `definition.dashboard.ticketsSold`
      | `definition.dashboard.ticketsReserved`
      | `definition.eventId`
      | `definition.wixFeeConfig.type`
      | `definition.salePeriod.hideNotOnSale`
      | `definition.saleStatus`
      | `definition.state`
      | `definition.pricing.pricingOptions.options`
      | `definition.pricing.pricingType`,
      5
    >
  >;
}

export function deleteTicketDefinition(
  httpClient: HttpClient
): DeleteTicketDefinitionSignature {
  return (eventId: string, options?: DeleteTicketDefinitionOptions) =>
    universalDeleteTicketDefinition(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTicketDefinitionSignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available. Use the [`deleteTicketDefinition()`](/ticket-definitions-v2/delete-ticket-definition) function instead.
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`deleteTicketDefinition()`](/ticket-definitions-v2/delete-ticket-definition).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.deleteTicketDefinition()`, and replace it with `ticketDefinitionsV2.deleteTicketDefinition()`. Update your code to work with the new `deleteTicketDefinition()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Deletes a ticket definition.
   * @param - Event ID.
   * @param - Details of tickets to delete.
   * @deprecated
   */
  (eventId: string, options?: DeleteTicketDefinitionOptions): Promise<void>;
}

export function changeCurrency(
  httpClient: HttpClient
): ChangeCurrencySignature {
  return (options?: NonNullablePaths<ChangeCurrencyOptions, `currency`, 2>) =>
    universalChangeCurrency(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangeCurrencySignature {
  /**
   * **Deprecated.** This function will continue to work until November 8, 2024, but a newer version is available at [`changeCurrency()`](/ticket-definitions-v2/change-currency).
   * >**Migration Instructions**.
   * > If this function is already in your code, it will continue to work. To stay compatible with future changes, migrate to [`changeCurrency()`](/ticket-definitions-v2/change-currency).
   * > To migrate to the new function:
   * > 1. Add the new import statement:
   * > ```js
   * > import { ticketDefinitionsV2 } from 'wix-events.v2';
   * > ```
   * > 2. Look for any code that uses `ticketDefinitions.changeCurrency()`, and replace it with `ticketDefinitionsV2.changeCurrency()`. Update your code to work with the new `changeCurrency()` response properties.
   * > 3. Test your changes to make sure your code behaves as expected.
   *
   * Changes the currency for all tickets per event.
   *
   * @deprecated
   */
  (
    options?: NonNullablePaths<ChangeCurrencyOptions, `currency`, 2>
  ): Promise<void>;
}

export const onTicketDefinitionCreated = EventDefinition(
  'wix.events.ticketing.events.TicketDefinitionCreated',
  false,
  (event: TicketDefinitionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }],
        },
      ])
    )
)<TicketDefinitionCreatedEnvelope>();
export const onTicketDefinitionDeleted = EventDefinition(
  'wix.events.ticketing.events.TicketDefinitionDeleted',
  false,
  (event: TicketDefinitionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }],
        },
      ])
    )
)<TicketDefinitionDeletedEnvelope>();
export const onTicketDefinitionUpdated = EventDefinition(
  'wix.events.ticketing.events.TicketDefinitionUpdated',
  false,
  (event: TicketDefinitionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }],
        },
      ])
    )
)<TicketDefinitionUpdatedEnvelope>();

export {
  AccountInfo,
  BaseEventMetadata,
  BulkCopyTicketDefinitionsByEventIdRequest,
  BulkCopyTicketDefinitionsByEventIdResponse,
  ById,
  ChangeCurrencyOptions,
  ChangeCurrencyRequest,
  ChangeCurrencyResponse,
  CopiedTicketDefinition,
  CreateTicketDefinitionOptions,
  CreateTicketDefinitionRequest,
  CreateTicketDefinitionResponse,
  CursorPaging,
  Cursors,
  Dashboard,
  DefinitionsQueryBuilder,
  DefinitionsQueryResult,
  DeleteTicketDefinitionOptions,
  DeleteTicketDefinitionOptionsDeleteOneOf,
  DeleteTicketDefinitionRequest,
  DeleteTicketDefinitionRequestDeleteOneOf,
  DeleteTicketDefinitionResponse,
  FacetCounts,
  FeeType,
  GetTicketDefinitionOptions,
  GetTicketDefinitionRequest,
  GetTicketDefinitionResponse,
  GroupBy,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListTicketDefinitionsOptions,
  ListTicketDefinitionsRequest,
  ListTicketDefinitionsResponse,
  MessageEnvelope,
  Money,
  OriginatedFrom,
  Paging,
  PagingMetadataV2,
  PaidTicketDefinitionExistsRequest,
  PaidTicketDefinitionExistsResponse,
  PricingOption,
  PricingOptions,
  QueryTicketDefinitionsOptions,
  QueryTicketDefinitionsRequest,
  QueryTicketDefinitionsResponse,
  QueryTicketDefinitionsV2Options,
  QueryTicketDefinitionsV2Request,
  QueryTicketDefinitionsV2Response,
  QueryV2,
  QueryV2PagingMethodOneOf,
  ResponseMetaData,
  SortOrder,
  Sorting,
  State,
  TicketDefinition,
  TicketDefinitionCreated,
  TicketDefinitionCreatedEnvelope,
  TicketDefinitionData,
  TicketDefinitionDeleted,
  TicketDefinitionDeletedEnvelope,
  TicketDefinitionFieldset,
  TicketDefinitionQuerySpec,
  TicketDefinitionStateEnumState,
  TicketDefinitionUpdated,
  TicketDefinitionUpdatedEnvelope,
  TicketPricing,
  TicketPricingPriceOneOf,
  TicketSalePeriod,
  TicketSaleStatus,
  Type,
  UpdateTicketDefinitionOptions,
  UpdateTicketDefinitionRequest,
  UpdateTicketDefinitionResponse,
  WebhookIdentityType,
  WixFeeConfig,
  utils,
} from './events-v1-ticket-definition-ticket-definitions.universal.js';
